import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination } from "../../components"

/* Widgets */
import { ManagerForm, ManagerPassword, ManagerHistory, ManagerActivity } from "../../widgets/Manager"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { list, remove } from "../../api/Manager"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Icons */
import { PlusIcon, EditIcon, KeyIcon, ClockIcon, PulseIcon, TrashIcon, LockIcon, GlobeIcon, UsersIcon, ROLE_ICONS } from "../../widgets/Manager/ManagerIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* Page Managers */
class Managers extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            titles: {},
            status: "loading",
            count: 0,
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const page = this.getPage()

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list(page).then((response) => {
            if (response.status === 200) {
                this.setState({ status: "ok", data: response.data.list, count: response.data.count, titles: response.data.titles })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0 })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0 })
            }
            else {
                this.setState({ status: "error", data: [], count: 0 })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0 })
        })

    }

    /* Remove Action */
    remove = (item) => {

        Swal.fire({
            title: t("swal.deleteConfirm"),
            text: `${item.name} (${item.email})`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f44336",
            reverseButtons: true,
            confirmButtonText: t("swal.deleteButton"),
            cancelButtonText: t("swal.cancelButton"),
        }).then((result) => {
            if (result.isConfirmed) {
                remove({ id: item.id }).then((response) => {
                    if (response.status === 200) {
                        this.load(true)
                        Swal.fire(utils.notification(t("manager.deletedSuccess"), "success"))
                    }
                    else if (response.data && response.data.message) {
                        Swal.fire(utils.notification(response.data.message, "error"))
                    }
                    else if (response.status === 498) {
                        Swal.fire(utils.notification(t("swal.invalidKey"), "error"))
                    }
                    else if (response.status === 401 || response.status === 403) {
                        Swal.fire(utils.notification(t("swal.noAccess"), "error"))
                    }
                    else {
                        Swal.fire(utils.notification(t("swal.generic"), "error"))
                    }
                }).catch(() => {
                    Swal.fire(utils.notification(t("swal.generic"), "error"))
                })
            }
        })
    }

    /* Draw Avatar — the role icon replaces the initials */
    _avatar = (item) => {

        /* Role Icon */
        const RoleIcon = ROLE_ICONS[item.access]

        return (
            <div className={`manager--avatar ${item.access}`} title={item.access}>
                {RoleIcon ? <RoleIcon size={20} /> : <UsersIcon size={20} />}
            </div>
        )
    }

    /* Draw Scope */
    _scope = (item) => {

        /* Global Roles */
        if (item.access === "superadmin" || item.access === "manager") {
            return <div className="manager--scope-global"><GlobeIcon />{t("manager.scopeGlobal")}</div>
        }

        /* Empty Scope */
        if (!item.studios && !item.operators) {
            return <div className="manager--scope-empty">{t("manager.scopeEmpty")}</div>
        }

        return (
            <>
                {item.studios &&
                    <div className="manager--scope-row">
                        <div className="manager--scope-label">{t("manager.studios")}</div>
                        <div className="manager--scope-value">{item.studios}</div>
                    </div>
                }
                {item.operators &&
                    <div className="manager--scope-row">
                        <div className="manager--scope-label">{t("manager.operators")}</div>
                        <div className="manager--scope-value">{item.operators}</div>
                    </div>
                }
            </>
        )
    }

    /* Draw Actions */
    _actions = (item, self) => {

        /* Superadmin — every change happens in the database only */
        if (item.access === "superadmin") {
            return (
                <div className="manager--action-locked">
                    <LockIcon />
                    <span>{t("manager.superadminLocked")}</span>
                </div>
            )
        }

        return (
            <>
                <div onClick={() => this._activity.open({ id: item.id, name: item.name })} className="manager--action" title={t("manager.activity")}>
                    <PulseIcon />
                </div>

                <div onClick={() => this._history.open({ id: item.id, name: item.name })} className="manager--action" title={t("manager.history")}>
                    <ClockIcon />
                </div>

                <div onClick={() => this._form.open({ type: "edit", id: item.id })} className="manager--action" title={t("manager.edit")}>
                    <EditIcon />
                </div>

                <div onClick={() => this._password.open({ id: item.id, name: item.name })} className="manager--action" title={t("manager.changePassword")}>
                    <KeyIcon />
                </div>

                {!self &&
                    <div onClick={() => this.remove(item)} className="manager--action red" title={t("manager.delete")}>
                        <TrashIcon />
                    </div>
                }
            </>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, titles } = this.state
        const { navigate, admin } = this.props
        const page = this.getPage()

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="manager--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="manager--page">

                {/* Head */}
                <div className="manager--table-header">
                    <div className="manager--column number">#</div>
                    <div className="manager--column person">{t("manager.name")}</div>
                    <div className="manager--column role">{t("manager.role")}</div>
                    <div className="manager--column scope">{t("manager.scope")}</div>
                    <div className="manager--column actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="manager--empty">
                        <UsersIcon />
                        <span>{t("manager.empty")}</span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) => {

                    /* Check Self */
                    const self = parseInt(item.id) === parseInt(admin.id)

                    return (
                        <div className={`manager--row ${self ? "self" : ""}`} key={`${index}`}>

                            <div className="manager--column number">{(page - 1) * 10 + index + 1}</div>

                            <div className="manager--column person">

                                {this._avatar(item)}

                                <div className="manager--person-text">
                                    <div className="manager--person-name">
                                        {item.name}
                                        {self ? <span className="manager--self-badge">{t("manager.you")}</span> : null}
                                    </div>
                                    <div className="manager--person-email">{item.email}</div>
                                </div>

                            </div>

                            <div className="manager--column role">
                                <div className={`manager--pill ${item.access}`}>
                                    <i />
                                    {titles[item.access] ? titles[item.access] : item.access}
                                </div>
                            </div>

                            <div className="manager--column scope">{this._scope(item)}</div>

                            <div className="manager--column actions">{this._actions(item, self)}</div>

                        </div>
                    )
                })}

                {/* Pagination */}
                <Pagination size={10} page={page} total={count} onPageChange={(page) => navigate(`/manager/${page}`)} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status } = this.state

        return (
            <div className="page">

                <div className="page-header">

                    <h1>{t("manager.title")}</h1>

                    {status === "ok" &&
                        <div onClick={() => this._form.open({ type: "create" })} className="button manager--add">
                            <PlusIcon size={14} />
                            <span>{t("common.add")}</span>
                        </div>
                    }

                </div>

                {/* Draw Content */}
                {this._content()}

                {/* Draw Form Modal */}
                <ManagerForm ref={(ref) => this._form = ref} reload={() => this.load()} />

                {/* Draw Password Modal */}
                <ManagerPassword ref={(ref) => this._password = ref} />

                {/* Draw History Modal */}
                <ManagerHistory ref={(ref) => this._history = ref} />

                {/* Draw Activity Modal */}
                <ManagerActivity ref={(ref) => this._activity = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Managers))
